/**
 * DOCX Viewer
 *
 * Renders original .docx files client-side using the docx-preview library.
 * Supports text-search highlighting for citation navigation.
 */

import { useEffect, useRef, useState } from 'react'
import { renderAsync } from 'docx-preview'
import { Loader2, AlertTriangle } from 'lucide-react'
import { highlightTextInDOM } from '@/lib/text-highlight'
import type { TextHighlight } from '@/stores/preview-store'

interface DocxViewerProps {
  fileUrl: string
  textHighlight?: TextHighlight | null
  zoom: number
}

export function DocxViewer({ fileUrl, textHighlight, zoom }: DocxViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadDocx() {
      setIsLoading(true)
      setError(null)

      try {
        const response = await fetch(fileUrl)
        if (!response.ok) throw new Error(`Failed to fetch DOCX: ${response.status}`)
        const arrayBuffer = await response.arrayBuffer()

        if (cancelled) return

        const iframe = iframeRef.current
        if (!iframe) return

        // Create a fresh document inside the iframe for style isolation
        const iframeDoc = iframe.contentDocument
        if (!iframeDoc) return

        iframeDoc.open()
        iframeDoc.write('<!DOCTYPE html><html><head><style>body { margin: 0; padding: 16px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }</style></head><body><div id="docx-container"></div></body></html>')
        iframeDoc.close()

        const container = iframeDoc.getElementById('docx-container')
        if (!container || cancelled) return

        await renderAsync(arrayBuffer, container, undefined, {
          inWrapper: false,
          ignoreWidth: false,
          ignoreHeight: false,
        })

        if (cancelled) return

        // Apply text highlight after rendering
        if (textHighlight?.chunkText) {
          // Small delay to let docx-preview finish DOM updates
          setTimeout(() => {
            if (!cancelled) {
              highlightTextInDOM(
                container,
                textHighlight.chunkText,
                textHighlight.sectionHeader,
                true
              )
            }
          }, 100)
        }

        setIsLoading(false)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load DOCX')
          setIsLoading(false)
        }
      }
    }

    void loadDocx()

    return () => {
      cancelled = true
    }
  }, [fileUrl, textHighlight])

  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading document...</p>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
          <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <iframe
        ref={iframeRef}
        sandbox="allow-same-origin"
        title="DOCX Preview"
        className="w-full h-full border-0"
        style={{
          zoom,
          display: isLoading || error ? 'none' : 'block',
        }}
      />
    </div>
  )
}
